import { useNavigate } from 'react-router-dom';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface SidebarItem {
  label: string;
  onClick: () => void;
  icon?: string;
  permissions?: string[];
  children?: SidebarItem[];
  isExpanded?: boolean;
}

export type UserRole = 'admin' | 'accountant' | 'sysadmin';

export interface UserAuth {
  role: UserRole;
  permissions: string[];
  userId?: string;
  username?: string;
  accountantOid?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const ROUTES = {
  ADMIN: {
    BASE: '/HomeScreen_AdminPanel',
    CUSTOMERS: '/HomeScreen_AdminPanel/hariltsagch',
    USERS: '/HomeScreen_AdminPanel/app-user',
    ACCOUNTANTS: '/HomeScreen_AdminPanel/accountant',
    SETTINGS: '/HomeScreen_AdminPanel/settings',
    USER_MANAGEMENT: '/HomeScreen_AdminPanel/users',
  },
  ACCOUNTANT: {
    BASE: '/main_accountant',
    CUSTOMERS: '/main_accountant/customers',
    USERS: '/main_accountant/app-user',
  }
} as const;

const ICONS = {
  FOLDER: '📁',
  USER: '👤',
  APP_USER: '👨‍💻',
  ACCOUNTANT: '👨‍💼',
  ACCOUNT: '🟣',
  LICENSE: '🟢',
  JOURNAL: '📝',
  GENERAL_JOURNAL: '📄',
  SALARY: '💰',
  SUPPLIER: '👥',
  BUSINESS_RELATIONS: '👥',
  BALANCE: '📄',
  GUIDE: '✅',
  RECEIPT: '🔺',
  SUMMARY_MONTH: '📄',
  SUMMARY_YEAR: '⚠️',
  WARNING: '⚠️',
  CUSTOMS: '📅',
  BLACKLIST: '❌',
  RESOLUTION_REPORT: '📄',
  CONSOLIDATED_REPORT: '📄',
  PEACE_OF_MIND: '🔍',
  TAX_DETAILS: '⏰',
  TAX_PAYMENT: '✅',
  ANALYTICS: '📊',
  DISPLAY: '🥧',
  REPORT: '📄',
  REPORT_1112: '📄',
  REPORT_1111: '📄',
  BALANCE_SHEET: '⚖️',
  INCOME_STATEMENT: '📈',
  CASH_FLOW: '💸',
  SETTINGS: '⚙️',
  COMPANY: '🏢',
  ExcelUpload: '📤',
} as const;

const ROLE_DISPLAY_NAMES = {
  admin: 'Админ',
  accountant: 'Нягтлан',
  sysadmin: 'Систем админ',
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const createSidebarItem = (
  label: string,
  onClick: () => void,
  icon: string,
  permissions?: string[],
  children?: SidebarItem[],
  isExpanded?: boolean
): SidebarItem => ({
  label,
  onClick,
  icon,
  permissions,
  children,
  isExpanded,
});

const createNavigationHandler = (
  navigate: (path: string) => void,
  onNavigate?: () => void
) => (path: string) => {
  navigate(path);
  onNavigate?.();
};

// ============================================================================
// SIDEBAR CONFIGURATIONS
// ============================================================================

const getAccountantListItems = (handleNavigation: (path: string) => void): SidebarItem[] => [
  createSidebarItem('Харилцагч', () => handleNavigation(ROUTES.ACCOUNTANT.CUSTOMERS), ICONS.USER, ['view_customers', 'manage_customers']),
  createSidebarItem('App хэрэглэгч', () => handleNavigation(ROUTES.ACCOUNTANT.USERS), ICONS.APP_USER, ['manage_users']),
 // createSidebarItem('Данс', () => handleNavigation('/main_accountant/dang'), ICONS.ACCOUNT, ['view_accounts']),
  //createSidebarItem('Дансны хуулга', () => handleNavigation('/main_accountant/license-copy'), ICONS.LICENSE, ['view_licenses']),
  //createSidebarItem('Журнал бичилт', () => handleNavigation('/main_accountant/journal-entry'), ICONS.JOURNAL, ['view_journal']),
  //createSidebarItem('Ерөнхий журнал', () => handleNavigation('/main_accountant/general-journal'), ICONS.GENERAL_JOURNAL, ['view_general_journal']),
  createSidebarItem('Цалин', () => handleNavigation('/main_accountant/salary'), ICONS.SALARY, ['view_salary']),
  //createSidebarItem('Нийлүүлэгч', () => handleNavigation('/main_accountant/netizen'), ICONS.SUPPLIER, ['view_netizen']),
  //createSidebarItem('Бизнес харилцагч', () => handleNavigation('/main_accountant/business-relations'), ICONS.BUSINESS_RELATIONS, ['view_business_relations']),
  //createSidebarItem('Эхний үлдэгдэл', () => handleNavigation('/main_accountant/remaining-balance'), ICONS.BALANCE, ['view_balance']),
  //createSidebarItem('Хаалтын гүйлгээ', () => handleNavigation('/main_accountant/pocket-guide'), ICONS.GUIDE, ['view_guide']),
];

const getAdminListItems = (handleNavigation: (path: string) => void): SidebarItem[] => [
  createSidebarItem('Нягтлан бодогч жагсаалт', () => handleNavigation(ROUTES.ADMIN.ACCOUNTANTS), ICONS.ACCOUNTANT, ['manage_accountants', 'view_accountants']),
  createSidebarItem('Харилцагч', () => handleNavigation('/HomeScreen_AdminPanel/hariltsagch'), ICONS.USER, ['view_customers', 'manage_customers']),
  createSidebarItem('Бизнес харилцагч', () => handleNavigation('/HomeScreen_AdminPanel/business-customer'), ICONS.USER, ['view_customers', 'manage_customers']),
  
  createSidebarItem('App хэрэглэгч', () => handleNavigation('/HomeScreen_AdminPanel/app-user'), ICONS.APP_USER, ['manage_users']),
  createSidebarItem('Данс', () => handleNavigation('/HomeScreen_AdminPanel/dang'), ICONS.ACCOUNT, ['view_accounts']),
  createSidebarItem('Дансны хуулга', () => handleNavigation('/HomeScreen_AdminPanel/license-copy'), ICONS.LICENSE, ['view_licenses']),
  createSidebarItem('Журнал бичилт', () => handleNavigation('/HomeScreen_AdminPanel/journal-entry'), ICONS.JOURNAL, ['view_journal']),
  createSidebarItem('Ерөнхий журнал', () => handleNavigation('/HomeScreen_AdminPanel/general-journal'), ICONS.GENERAL_JOURNAL, ['view_general_journal']),
  createSidebarItem('Цалин', () => handleNavigation('/HomeScreen_AdminPanel/salary'), ICONS.SALARY, ['view_salary']),
  createSidebarItem('Нийлүүлэгч', () => handleNavigation('/HomeScreen_AdminPanel/netizen'), ICONS.SUPPLIER, ['view_netizen']),
  createSidebarItem('Бизнес харилцагч', () => handleNavigation('/HomeScreen_AdminPanel/business-relations'), ICONS.BUSINESS_RELATIONS, ['view_business_relations']),
  //createSidebarItem('Эхний үлдэгдэл', () => handleNavigation('/HomeScreen_AdminPanel/remaining-balance'), ICONS.BALANCE, ['view_balance']), // Санхүү тайлан дотор байна
  createSidebarItem('Хаалтын гүйлгээ', () => handleNavigation('/HomeScreen_AdminPanel/pocket-guide'), ICONS.GUIDE, ['view_guide']),
];

const getEBarimtItems = (isAdmin: boolean, handleNavigation: (path: string) => void): SidebarItem[] => {
  const basePath = isAdmin ? '/HomeScreen_AdminPanel' : '/main_accountant';
  return [
   createSidebarItem('Excel файл Upload', () => handleNavigation(`${basePath}/ExcelUpload`), ICONS.ExcelUpload, undefined), // No permissions required
   createSidebarItem('Татваргүй баримт Excel', () => handleNavigation(`${basePath}/TaxfreeExcelUpload`), ICONS.ExcelUpload, undefined),
   createSidebarItem('Худалдан авалт', () => handleNavigation(`${basePath}/Purchase`), ICONS.RECEIPT, undefined),
   createSidebarItem('Борлуулалт', () => handleNavigation(`${basePath}/Sales`), ICONS.RECEIPT, undefined),
   createSidebarItem('Журналд холбогдоогүй баримт', () => handleNavigation(`${basePath}/no-ledger-transaction-list`), ICONS.RECEIPT, undefined),
   createSidebarItem('Товчоо сараар', () => handleNavigation(`${basePath}/summary-month`), ICONS.SUMMARY_MONTH, ['view_summary_month']),
  createSidebarItem('Товчоо жилээр', () => handleNavigation(`${basePath}/summary-year`), ICONS.SUMMARY_YEAR, ['view_summary_year']),
   // createSidebarItem('Анхаарах баримт', () => handleNavigation(`${basePath}/${isAdmin ? 'warning-document' : 'warning-garnet'}`), ICONS.WARNING, ['view_warnings']),
   // createSidebarItem('Гаалийн мэдээ', () => handleNavigation(`${basePath}/${isAdmin ? 'customs-info' : 'customs-exemption'}`), ICONS.CUSTOMS, ['view_customs']),
   // createSidebarItem('Хар жагсаалт', () => handleNavigation(`${basePath}/blacklist`), ICONS.BLACKLIST, ['view_blacklist']),
  ];
};

const getTaxReportItems = (isAdmin: boolean, handleNavigation: (path: string) => void): SidebarItem[] => {
  const basePath = isAdmin ? '/HomeScreen_AdminPanel' : '/main_accountant';
  return [
    createSidebarItem('Тушаах тайлан', () => handleNavigation(`${basePath}/${isAdmin ? 'resolution-report' : 'TaxGetList'}`), ICONS.RESOLUTION_REPORT, ['view_resolution_report']),
    createSidebarItem('Татварын нэхэмжлэх', () => handleNavigation(`${basePath}/tax-invoice-balance`), ICONS.RESOLUTION_REPORT, ['view_resolution_report']),
    //createSidebarItem('Нэгтгэсэн тайлан', () => handleNavigation(`${basePath}/consolidated-report`), ICONS.CONSOLIDATED_REPORT, ['view_consolidated_report']),
    createSidebarItem(isAdmin ? 'Тайвангийн түүх' : 'Тайлангийн түүх', () => handleNavigation(`${basePath}/${isAdmin ? 'peace-of-mind' : 'TaxGetHistory'}`), ICONS.PEACE_OF_MIND, ['TaxGetHistory']),
    //createSidebarItem('Татварын дэлгэрэнгүй', () => handleNavigation(`${basePath}/tax-details`), ICONS.TAX_DETAILS, ['view_tax_details']),
    createSidebarItem('Татвар төлөлт', () => handleNavigation(`${basePath}/tax-payment`), ICONS.TAX_PAYMENT, ['view_tax_payment']),
    //createSidebarItem('Аналитик', () => handleNavigation(`${basePath}/analytics`), ICONS.ANALYTICS, ['view_analytics']),
    //createSidebarItem('Дэлгэц', () => handleNavigation(`${basePath}/display`), ICONS.DISPLAY, ['view_display']),
    //createSidebarItem('Тайлан', () => handleNavigation(`${basePath}/report`), ICONS.REPORT, ['view_report']),
    createSidebarItem('ХХОАТ тайлан', () => handleNavigation(`${basePath}/tax-post-hhoat`), ICONS.REPORT_1112),
    //createSidebarItem('1111 тайлангийн утга', () => handleNavigation(`${basePath}/1111-report`), ICONS.REPORT_1111, ['view_1111_report']),
  ];
};

const getFinancialReportItems = (isAdmin: boolean, handleNavigation: (path: string) => void): SidebarItem[] => {
  const basePath = isAdmin ? '/HomeScreen_AdminPanel' : '/main_accountant';
  const items = [
    createSidebarItem('Эхний үлдэгдэл', () => handleNavigation(`${basePath}/account-period-balance`), ICONS.BALANCE, undefined), // Permission optional for now
    createSidebarItem('Ерөнхий журнал', () => handleNavigation(`${basePath}/ledger`), ICONS.BALANCE, undefined), // Permission optional for now
    createSidebarItem('Хаалт хийх', () => handleNavigation(`${basePath}/account-period-close`), ICONS.BALANCE, undefined), // Permission optional for now
    createSidebarItem('Мөнгөн гүйлгээний тайлан', () => handleNavigation(`${basePath}/cashflow`), ICONS.BALANCE, undefined),
    //createSidebarItem('Баланс', () => handleNavigation(`${basePath}/balance`), ICONS.BALANCE_SHEET, ['view_balance_sheet']),
    //createSidebarItem('Орлогын тайлан', () => handleNavigation(`${basePath}/income-statement`), ICONS.INCOME_STATEMENT, ['view_income_statement']),
    //createSidebarItem('Мөнгөн хөрөнгийн урсгал', () => handleNavigation(`${basePath}/cash-flow`), ICONS.CASH_FLOW, ['view_cash_flow']),
  ];
  
  // Debug logging for production issues
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 getFinancialReportItems:', {
      isAdmin,
      basePath,
      itemsCount: items.length,
      items: items.map(item => ({ label: item.label, permissions: item.permissions }))
    });
  }
  
  return items;
};

const getInsuranceReport = (isAdmin: boolean, handleNavigation: (path: string) => void): SidebarItem[] => {
  const basePath = isAdmin ? '/HomeScreen_AdminPanel' : '/main_accountant';
  return [
    createSidebarItem('НДШ тайлангийн түүх', () => handleNavigation(`${basePath}/insurance-report-summary`), ICONS.REPORT, ['view_insurance_report']),
    //createSidebarItem('Харилцагчдын history list', () => handleNavigation(`${basePath}/insurance-customers-history`), ICONS.REPORT, ['view_insurance_report']),
    //createSidebarItem('Харилцагчдын жагсаалт', () => handleNavigation(`${basePath}/insurance-customers`), ICONS.USER, ['view_insurance_report']),
  ];
};

// ============================================================================
// MAIN HOOK
// ============================================================================

export const useSidebarConfig = (userAuth: UserAuth, onNavigate?: () => void) => {
  const navigate = useNavigate();
  const handleNavigation = createNavigationHandler(navigate, onNavigate);
  const isAdmin = userAuth.role === 'admin' || userAuth.role === 'sysadmin';

  const hasPermission = (requiredPermissions?: string[]): boolean => {
    // Accountants should see all menu items regardless of explicit permissions
    if (userAuth.role === 'accountant') {
      return true;
    }
    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }
    const hasAccess = requiredPermissions.some(permission => 
      userAuth.permissions.includes(permission)
    );
    
    // Debug logging for production issues
    if (process.env.NODE_ENV === 'development') {
      if (!hasAccess && requiredPermissions.includes('view_account_period_balance')) {
        console.log('🔍 Permission check for Эхний үлдэгдэл:', {
          requiredPermissions,
          userPermissions: userAuth.permissions,
          hasAccess
        });
      }
    }
    
    return hasAccess;
  };

  const getSidebarItems = (): SidebarItem[] => {
    const baseItems: SidebarItem[] = [
      createSidebarItem(
        'Жагсаалт',
        () => {},
        ICONS.FOLDER,
        undefined,
        isAdmin ? getAdminListItems(handleNavigation) : getAccountantListItems(handleNavigation),
        true  // Default expanded - Бүх цонхийг харуулах
      ),
      createSidebarItem(
        'И-Баримт',
        () => {},
        ICONS.FOLDER,
        undefined,
        getEBarimtItems(isAdmin, handleNavigation),
        true  // Default expanded - Бүх цонхийг харуулах
      ),
      createSidebarItem(
        'Татварын тайлан',
        () => {},
        ICONS.GENERAL_JOURNAL,
        undefined,
        getTaxReportItems(isAdmin, handleNavigation),
        true  // Default expanded - Бүх цонхийг харуулах
      ),  
      createSidebarItem(
        'Нийгмийн даатгал',
        () => {},
        ICONS.GENERAL_JOURNAL,
        undefined,
        getInsuranceReport(isAdmin, handleNavigation),
        true  // Default expanded - Бүх цонхийг харуулах
      ),
      createSidebarItem(
        'Санхүү тайлан',
        () => {},
        ICONS.GENERAL_JOURNAL,
        undefined,
        getFinancialReportItems(isAdmin, handleNavigation),
        true  // Default expanded - Бүх цонхийг харуулах
      ),
    ];

    // Add Тохиргоо folder for admin
    if (isAdmin) {
      baseItems.push(
        createSidebarItem(
          'Тохиргоо',
          () => {},
          ICONS.FOLDER,
          undefined,
          [
            createSidebarItem('Гүйлгээний загвар', () => handleNavigation('/HomeScreen_AdminPanel/template-ledger'), ICONS.JOURNAL, undefined),
          ],
          true  // Default expanded
        )
      );
    }

    // Add admin-specific items
    if (isAdmin) {
      baseItems.push(
        createSidebarItem('Системийн тохиргоо', () => handleNavigation(ROUTES.ADMIN.SETTINGS), ICONS.SETTINGS, ['system_settings']),
        createSidebarItem('Хэрэглэгчийн удирдлага', () => handleNavigation(ROUTES.ADMIN.USER_MANAGEMENT), ICONS.USER, ['manage_users'])
      );
    }

    // Filter items based on permissions, including children
    const filteredItems = baseItems
      .filter(item => hasPermission(item.permissions))
      .map(item => {
        // If item has children, filter them too
        if (item.children && item.children.length > 0) {
          const filteredChildren = item.children.filter(child => {
            const hasAccess = hasPermission(child.permissions);
            // Debug logging for ExcelUpload menu item
            if (child.label === 'Excel файл Upload' || child.label === 'ExcelUpload') {
              console.log('🔍 ExcelUpload menu item permission check:', {
                label: child.label,
                permissions: child.permissions,
                hasAccess,
                userRole: userAuth.role,
                userPermissions: userAuth.permissions
              });
            }
            return hasAccess;
          });
          return {
            ...item,
            children: filteredChildren.length > 0 ? filteredChildren : undefined
          };
        }
        return item;
      })
      .filter(item => {
        // Remove items that have no children after filtering (if they were supposed to have children)
        if (item.children !== undefined && (!item.children || item.children.length === 0)) {
          return false;
        }
        return true;
      });
    
    // Debug logging for EBarimt items
    const ebarimtItem = filteredItems.find(item => item.label === 'И-Баримт');
    if (ebarimtItem) {
      console.log('🔍 И-Баримт menu item:', {
        label: ebarimtItem.label,
        childrenCount: ebarimtItem.children?.length || 0,
        children: ebarimtItem.children?.map(c => c.label) || []
      });
    }
    
    return filteredItems;
  };

  const getSidebarTitleButton = (): SidebarItem => ({
    label: 'NovaQ',
    onClick: () => handleNavigation(isAdmin ? ROUTES.ADMIN.BASE : ROUTES.ACCOUNTANT.BASE),
    icon: ICONS.COMPANY,
  });

  const getUserDisplayName = (): string => {
    return userAuth.username || userAuth.userId || 'Хэрэглэгч';
  };

  const getUserRoleDisplay = (): string => {
    return ROLE_DISPLAY_NAMES[userAuth.role] || 'Хэрэглэгч';
  };

  return {
    sidebarItems: getSidebarItems(),
    sidebarTitleButton: getSidebarTitleButton(),
    userDisplayName: getUserDisplayName(),
    userRoleDisplay: getUserRoleDisplay(),
    userAuth,
  };
};